
import React, { useState } from "react";
import moment, { Moment } from "moment";
import { intl } from "LocaleWrapper";

import { Select } from "@cofano/cck";
import { Box, IconButton, Popover } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import { FormattedDate } from "utils/DateUtils";
import { PeriodWindow } from "./PeriodField";

export const getWeekWindow = (date?: Date | number | Moment | null): PeriodWindow => ({
	from: moment(date ?? undefined).startOf("isoWeek"),
	to: moment(date ?? undefined).endOf("isoWeek"),
});

const WeekField = ({
	onChange,
	defaultStart,
	simple,
	disabled,
}: {
	onChange: (window: { from: Moment, to: Moment }) => void;
	defaultStart?: Date | number | Moment;
	simple?: boolean;
	disabled?: boolean;
}) => {

	const [ current, setCurrent ] = useState({
		week: defaultStart ? moment(defaultStart).isoWeek() : moment().isoWeek(),
		year: defaultStart ? moment(defaultStart).isoWeekYear() : moment().isoWeekYear(),
	});

	const [ anchorEl, setAnchorEl ] = useState<HTMLElement | null>(null);

	const [ window, setWindow ] = useState({
		start: defaultStart ? moment(defaultStart).startOf("isoWeek") : moment().startOf("isoWeek"),
		end: defaultStart ? moment(defaultStart).endOf("isoWeek") : moment().endOf("isoWeek"),
	});

	const isoWeek = moment().year(current.year).endOf("year").isoWeek();
	const weeksInYear = isoWeek === 1 ? 52 : isoWeek;

	const weekOptions = [ ...Array(weeksInYear).keys() ]
		.map(number => ({ id: number + 1, name: `${number + 1}` }));

	const yearOptions = [ {
		id: moment().add(-1, "years").year(),
		name: `${moment().add(-1, "years").year()}`,
	}, {
		id: moment().year(),
		name: `${moment().year()}`,
	}, {
		id: moment().add(1, "years").year(),
		name: `${moment().add(1, "years").year()}`,
	} ];

	const handleChange = (newDate: Moment) => {
		setCurrent({
			week: moment(newDate).isoWeek(),
			year: moment(newDate).year(),
		});

		setWindow({
			start: moment(newDate).startOf("isoWeek"),
			end: moment(newDate).endOf("isoWeek"),
		});

		onChange({
			from: moment(newDate).startOf("isoWeek"),
			to: moment(newDate).endOf("isoWeek"),
		});
	};

	const handleCustomChange = (newWeek: number, newYear: number) => {
		setAnchorEl(null);
		setCurrent({
			week: newWeek,
			year: newYear,
		});

		const newDate = moment().isoWeek(newWeek).year(newYear).startOf("isoWeek");
		setWindow({
			start: moment(newDate).startOf("isoWeek"),
			end: moment(newDate).endOf("isoWeek"),
		});

		onChange({
			from: moment(newDate).startOf("isoWeek"),
			to: moment(newDate).endOf("isoWeek"),
		});
	};

	const open = Boolean(anchorEl);
	const id = open ? "simple-popover" : undefined;

	return (
		!simple ? (
			<React.Fragment>
				<Box sx={{ alignItems: "center", display: "flex", gap: 1 }} py={0.2} px={0.5}>
					<IconButton
						disabled={disabled}
						onClick={() => handleChange(moment(window.start).add(-7, "days"))}
						size="small">
						<KeyboardArrowLeftIcon />
					</IconButton>

					<Box
						onClick={(event) => setAnchorEl(event.currentTarget)}
						sx={{
							alignItems: "center",
							display: "flex",
							fontSize: "12px",

							"&:hover": {
								cursor: "pointer",
								textDecoration: "underline",
							},
						}}>
						<span>{intl.formatMessage({ id: "CONTRACT_WEEK" })}</span>
						<span>&nbsp;</span>
						<strong>{current.week}</strong>
						<span>&nbsp;</span>
						<span>{intl.formatMessage({ id: "FROM" }).toLowerCase()}</span>
						<span>&nbsp;</span>
						<strong><FormattedDate date={window.start} format="DD/MM/YY" /></strong>
						<span>&nbsp;</span>
						<span>{intl.formatMessage({ id: "TO" }).toLowerCase()}</span>
						<span>&nbsp;</span>
						<strong><FormattedDate date={window.end} format="DD/MM/YY" /></strong>
					</Box>

					<IconButton
						disabled={disabled}
						onClick={() => handleChange(moment(window.start).add(7, "days"))}
						size="small">
						<KeyboardArrowRightIcon />
					</IconButton>
				</Box>

				<Popover
					id={id}
					open={open}
					anchorEl={anchorEl}
					onClose={() => setAnchorEl(null)}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "center",
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "center",
					}}>
					<Box sx={{ display: "flex", gap: 2, width: "300px"  }} p={2}>
						<Select
							label="Week"
							fetchOptions={weekOptions}
							onChange={(option) => handleCustomChange(option?.id ?? 0, current.year)}
							getOptionLabel={option => option.name}
							clearable={false}
							value={weekOptions.find(option => option.id === current.week)} />

						<Select
							label="Jaar"
							fetchOptions={yearOptions}
							onChange={(option) => handleCustomChange(current.week, option?.id ?? 0)}
							getOptionLabel={option => option.name}
							clearable={false}
							value={yearOptions.find(option => option.id === current.year)} />
					</Box>
				</Popover>
			</React.Fragment>
		) : (
			<React.Fragment>
				<Box sx={{ display: "flex", gap: 2  }}>
					<Select
						label="Week"
						fetchOptions={weekOptions}
						onChange={(option) => handleCustomChange(option?.id ?? 0, current.year)}
						getOptionLabel={option => option.name}
						clearable={false}
						value={weekOptions.find(option => option.id === current.week)} />

					<Select
						label="Jaar"
						fetchOptions={yearOptions}
						onChange={(option) => handleCustomChange(current.week, option?.id ?? 0)}
						getOptionLabel={option => option.name}
						clearable={false}
						value={yearOptions.find(option => option.id === current.year)} />
				</Box>
			</React.Fragment>
		)
	);
};

export default WeekField;
